import { ICsvData } from '@/interfaces/storageTracking/ICsvData.ts';
import { ICompanyWithInfo } from '@/interfaces/storageTracking/ICompanyWithInfo.ts';
import { ITimelineByYearItem } from '@/interfaces/storageTracking/ITimelineObject.ts';

type TStatuses = keyof Pick<ICsvData,
'awaitingConstruction' |
'cancelledOrAbandoned' |
'decommissioned' |
'feasibility' |
'finalInvestmentDecision' |
'financed' |
'forecast' |
'inDevelopment' |
'initialAnnoucement' |
'operational' |
'planningPermissionApplication' |
'planningApplicationRefused' |
'planningApplicationWithdrawn' |
'planningApplicationApproved' |
'planningApplicationExpired' |
'planningApplicationSubmitted' |
'publicFundingApplicationSubmitted' |
'publicFundingConfirmed' |
'networkConnectionApproval' |
'networkConnectionApplicationSubmitted' |
'networkConnectionApproved' |
'networkConnectionDate' |
'networkConnectionMade' |
'construction' |
'constructed' |
'live' |
'trading' |
'tradingInternally' |
'tradingExternally' |
'postOperational' |
'complete' |
'upscaled' |
'capacityMarketContractAwarded' |
'capacityMarketContractDelivery' |
'capacityMarketContractEnd' |
'storageLicenceApproved' |
'environmentalImpactAssessmentSubmitted' |
'environmentalImpactAssessmentApproved' |
'underConstruction'
>;

const statusMapping: Record<TStatuses, string> = {
    awaitingConstruction: 'Awaiting construction',
    cancelledOrAbandoned: 'Cancelled or abandoned',
    decommissioned: 'Decommissioned',
    feasibility: 'Feasibility',
    finalInvestmentDecision: 'Final investment decision',
    financed: 'Financed',
    forecast: 'Forecast',
    inDevelopment: 'In development',
    initialAnnoucement: 'Initial announcement',
    operational: 'Operational',
    planningPermissionApplication: 'Planning permission application',
    planningApplicationRefused: 'Planning application refused',
    planningApplicationWithdrawn: 'Planning application withdrawn',
    planningApplicationApproved: 'Planning application approved',
    planningApplicationExpired: 'Planning application expired',
    planningApplicationSubmitted: 'Planning application submitted',
    publicFundingApplicationSubmitted: 'Public funding application submitted',
    publicFundingConfirmed: 'Public funding confirmed',
    networkConnectionApproval: 'Network connection approval',
    networkConnectionApplicationSubmitted: 'Network connection application submitted',
    networkConnectionApproved: 'Network connection approved',
    networkConnectionDate: 'Network connection date',
    networkConnectionMade: 'Network connection made',
    construction: 'Construction',
    constructed: 'Constructed',
    live: 'Live',
    trading: 'Trading',
    tradingInternally: 'Trading internally',
    tradingExternally: 'Trading externally',
    postOperational: 'Post operational',
    complete: 'Complete',
    upscaled: 'Upscaled',
    capacityMarketContractAwarded: 'Capacity market contract awarded',
    capacityMarketContractDelivery: 'Capacity market contract delivery',
    capacityMarketContractEnd: 'Capacity market contract end',
    storageLicenceApproved: 'Storage licence approved',
    environmentalImpactAssessmentSubmitted: 'Environmental impact assessment submitted',
    environmentalImpactAssessmentApproved: 'Environmental impact assessment approved',
    underConstruction: 'Under construction',
};

const statusesToCheck: TStatuses[] = Object.keys(statusMapping) as TStatuses[];

const statusDateList: Record<string, string> = {
    latestAwaitingConstruction: 'Awaiting construction',
    latestCancelledOrAbandoned: 'Cancelled or abandoned',
    latestDecommissioned: 'Decommissioned',
    latestFeasibility: 'Feasibility',
    latestFinanced: 'Financed',
    latestForecastDate: 'Forecast',
    latestInitialAnnoucement: 'Initial announcement',
    latestOperationalDate: 'Operational',
    latestPlanningApplicationApproved: 'Planning application approved',
    latestPlanningApplicationExpired: 'Planning application expired',
    latestPlanningApplicationSubmitted: 'Planning application submitted',
    latestPublicFundingConfirmed: 'Public funding confirmed',
    latestTrading: 'Trading',
    latestUnderConstruction: 'Under construction',
    latestInDevelopment: 'In development',
    latestNetworkConnectionDate: 'Target network connection date',
    latestPlanningApplicationWithdrawn: 'Planning application withdrawn',
    latestUpscaled: 'Upscaled',
    latestComplete: 'Complete',
    latestTradingExternally: 'Trading externally',
    latestTradingInternally: 'Trading internally',
    latestLive: 'Live',
    latestNetworkConnectionMade: 'Network connection made',
    latestConstruction: 'Construction',
    latestConstructed: 'Constructed',
    latestNetworkConnectionApproved: 'Network connection approved',
    latestNetworkConnectionApplicationSubmitted: 'Network connection application submitted',
    latestNetworkConnectionApproval: 'Network connection approval',
    latestFinalInvestmentDecision: 'Final investment decision',
    latestPublicFundingApplicationSubmitted: 'Public funding application submitted',
    latestPlanningApplicationRefused: 'Planning application refused',
    latestPlanningPermissionApplication: 'Planning permission application',
    latestPostOperational: 'Post operational',
    latestCapacityMarketContractAwarded: 'Capacity market contract awarded',
    latestCapacityMarketContractDelivery: 'First Capacity market contract delivery year',
    latestCapacityMarketContractEnd: 'Capacity market contract end year',
    latestStorageLicenceApproved: 'Storage licence approved',
    latestEnvironmentalImpactAssessmentSubmitted: 'Environmental impact assessment submitted',
    latestEnvironmentalImpactAssessmentApproved: 'Environmental impact assessment approved',
};

// const statusYearList = {
//     latestAwaitingConstructionYear: 'Awaiting construction',
//     latestCancelledOrAbandonedYear: 'Cancelled or abandoned',
//     latestDecommissionedYear: 'Decommissioned',
//     latestFeasibilityYear: 'Feasibility',
//     latestFinancedYear: 'Financed',
//     forecastYear: 'Forecast',
//     latestInitialAnnoucementYear: 'Initial announcement',
//     operationalYear: 'Operational',
//     latestPlanningApplicationApprovedYear: 'Planning application approved',
//     latestPlanningApplicationExpiredYear: 'Planning application expired',
//     latestPlanningApplicationSubmittedYear: 'Planning application submitted',
//     latestPublicFundingConfirmedYear: 'Public funding confirmed',
//     latestTradingYear: 'Trading',
//     latestUnderConstructionYear: 'Under construction',
//     latestInDevelopmentYear: 'In development',
//     latestNetworkConnectionDateYear: 'Target network connection date',
//     latestPlanningApplicationWithdrawnYear: 'Planning application withdrawn',
//     latestUpscaledYear: 'Upscaled',
//     latestCompleteYear: 'Complete',
//     latestTradingExternallyYear: 'Trading externally',
//     latestTradingInternallyYear: 'Trading internally',
//     latestLiveYear: 'Live',
//     latestNetworkConnectionMadeYear: 'Network connection made',
//     latestConstructionYear: 'Construction',
//     latestConstructedYear: 'Constructed',
//     latestNetworkConnectionApprovedYear: 'Network connection approved',
//     latestNetworkConnectionApplicationSubmittedYear: 'Network connection application submitted',
//     latestNetworkConnectionApprovalYear: 'Network connection approval',
//     latestFinalInvestmentDecisionYear: 'Final investment decision',
//     latestPublicFundingApplicationSubmittedYear: 'Public funding application submitted',
//     latestPlanningApplicationRefusedYear: 'Planning application refused',
//     latestPlanningPermissionApplicationYear: 'Planning permission application',
//     latestPostOperationalYear: 'Post operational',
//     latestCapacityMarketContractAwardedYear: 'Capacity market contract awarded',
//     latestCapacityMarketContractDeliveryYear: 'Capacity market contract delivery year',
//     latestCapacityMarketContractEndYear: 'Capacity market contract end year',
//     latestStorageLicenceApprovedYear: 'Storage licence approved',
//     latestEnvironmentalImpactAssessmentSubmittedYear: 'Environmental impact assessment submitted',
//     latestEnvironmentalImpactAssessmentApprovedYear: 'Environmental impact assessment approved',
// };

const roleList: Record<string, string> = {
    latestOwner: 'Owner',
    latestDeveloper: 'Developer',
    latestFinancer: 'Financer',
    latestTrader: 'Trader',
    latestTechnologyProvider: 'Technology provider',
    latestOperator: 'Operator',
};

function isLastDayOfQuarter (month: number, day: number): boolean {
    return (month === 3 && day === 31)
    || (month === 6 && day === 30)
    || (month === 9 && day === 30)
    || (month === 12 && day === 30);
}

function formatSmartDate (date: Date): string {
    const day = date.getDate();
    const month = date.getMonth() + 1; // add 1 to adjust for zero indexed months
    const year = date.getFullYear();

    if (isLastDayOfQuarter(month, day)) {
        const quarter = Math.ceil(month / 3);
        return `Q${quarter} ${year}`;
    } if (month === 12 && day === 31) {
        return `${year}`; // If date is just 2025 for example, it will be in the DB as 31/12/2025
    }
    return `${day}/${month}/${year}`;
}

function getAllStatusesOffProject (project: ICsvData): Array<{ status: string; date: Date }> {
    const allLatestStatuses: Array<{ status: string; date: Date }> = [];
    Object.entries(project.latestData).forEach(([key, value]) => {
        if (statusDateList[key] && value) {
            allLatestStatuses.push({ status: statusDateList[key], date: new Date(value) });
        }
    });

    return allLatestStatuses.sort((a, b) => a.date.getTime() - b.date.getTime());
}

function formatAllStatusesYear (project: ICsvData): Array<string> {
    if (project) {
        const activeStatus = getAllStatusesOffProject(project);
        return activeStatus.map((i) => `${i.status} - ${i.date.getFullYear()}`);
    }
    return [];
}

function formatAllStatusesSmart (project: ICsvData): Array<string> {
    if (project) {
        const activeStatus = getAllStatusesOffProject(project);

        return activeStatus.map((i) => `${i.status} - ${formatSmartDate(i.date)}`);
    }
    return [];
}

function formatStatusesAndRolesForTimelineByYear (project: ICsvData): Array<ITimelineByYearItem> {
    if (!project) return [];

    const events: Array<{ description: string; date: Date }> = [];

    const allStatusesFromProject = getAllStatusesOffProject(project);
    allStatusesFromProject.forEach((i) => {
        events.push({
            description: i.status,
            date: i.date,
        });
    });

    Object.entries(project.latestData).forEach(([key, value]) => {
        if (roleList[key] && value.date) {
            const dateOfCompanies = new Date(value.date);
            const companiesOnDate = value.companies
                .map((companyObj: ICompanyWithInfo) => companyObj.company)
                .join(', ');
            events.push({
                description: `${roleList[key]}: ${companiesOnDate}`,
                date: dateOfCompanies,
            });
        }
    });

    events.sort((a, b) => a.date.getTime() - b.date.getTime());

    const groupedEvents: Record<string, Array<{ description: string; date: Date }>> = {};
    events.forEach((event) => {
        const year = event.date.getFullYear().toString();
        if (!groupedEvents[year]) {
            groupedEvents[year] = [];
        }
        groupedEvents[year].push(event);
    });

    return Object.entries(groupedEvents).map(([year, eventsLocal]) => ({
        year,
        events: eventsLocal.map(e => ({
            description: e.description,
            date: formatSmartDate(e.date),
        })),
    }));
}

export {
    statusDateList,
    formatAllStatusesYear,
    formatAllStatusesSmart,
    formatStatusesAndRolesForTimelineByYear,
    statusesToCheck,
    statusMapping,
    type TStatuses,
};
